import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface SheetBaseContextValue {
  isMobile: boolean;
}

const SheetBaseContext = React.createContext<SheetBaseContextValue>({
  isMobile: false,
});

export function useSheetBase() {
  return React.useContext(SheetBaseContext);
}

export function SheetBaseHeader({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  const { isMobile } = useSheetBase();
  if (isMobile) {
    return (
      <SheetHeader className={cn("p-0 pb-3 text-left", className)} {...props}>
        {children}
      </SheetHeader>
    );
  }
  return (
    <DialogHeader className={cn("text-left", className)} {...props}>
      {children}
    </DialogHeader>
  );
}

export function SheetBaseTitle({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogTitle>) {
  const { isMobile } = useSheetBase();
  if (isMobile) {
    return (
      <SheetTitle className={className} {...props}>
        {children}
      </SheetTitle>
    );
  }
  return (
    <DialogTitle className={className} {...props}>
      {children}
    </DialogTitle>
  );
}

export function SheetBaseDescription({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogDescription>) {
  const { isMobile } = useSheetBase();
  if (isMobile) {
    return (
      <SheetDescription className={className} {...props}>
        {children}
      </SheetDescription>
    );
  }
  return (
    <DialogDescription className={className} {...props}>
      {children}
    </DialogDescription>
  );
}

export function SheetBaseBody({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("-m-1 min-h-0 flex-1 overflow-y-auto p-1", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function SheetBaseFooter({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  const { isMobile } = useSheetBase();
  if (isMobile) {
    return (
      <SheetFooter
        className={cn(
          "flex-row justify-end gap-2 border-t p-0 pt-3",
          className,
        )}
        {...props}
      >
        {children}
      </SheetFooter>
    );
  }
  return (
    <DialogFooter
      className={cn(
        "flex flex-row justify-end gap-2 border-t pt-3 mt-auto",
        className,
      )}
      {...props}
    >
      {children}
    </DialogFooter>
  );
}

export interface SheetBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Additional className applied to DialogContent on desktop.
   * Typically used for modal width, e.g. "sm:max-w-5xl", "sm:max-w-md", etc.
   */
  className?: string;
  /** Class override for desktop DialogContent */
  dialogClassName?: string;
  /** Class override for mobile SheetContent */
  sheetClassName?: string;
  /** Class applied to header container */
  headerClassName?: string;
  /** Class applied to footer container */
  footerClassName?: string;
  /** Class applied to inner body container when scrollable is true */
  bodyClassName?: string;
  /**
   * When true (default), wraps children in a scrollable container (`min-h-0 flex-1 overflow-y-auto`).
   * Set to false if children manage their own scrolling.
   */
  scrollable?: boolean;
  /**
   * Prevents inputs/keyboards from auto-focusing on sheet open on mobile (default: true).
   */
  preventAutoFocus?: boolean;
  /**
   * Bottom sheet placement on mobile (default: "bottom").
   */
  side?: "top" | "bottom" | "left" | "right";
  /**
   * Override viewport check if needed.
   */
  isMobile?: boolean;
  /**
   * Whether to show close X button (default: true).
   */
  showCloseButton?: boolean;
}

export function SheetBase({
  open,
  onOpenChange,
  title,
  description,
  header,
  footer,
  children,
  className,
  dialogClassName,
  sheetClassName,
  headerClassName,
  footerClassName,
  bodyClassName,
  scrollable = true,
  preventAutoFocus = true,
  side = "bottom",
  isMobile: isMobileOverride,
  showCloseButton = true,
}: SheetBaseProps) {
  const detectedIsMobile = useIsMobile();
  const isMobile = isMobileOverride ?? detectedIsMobile;

  const contextValue = React.useMemo(() => ({ isMobile }), [isMobile]);

  const bodyContent = scrollable ? (
    <SheetBaseBody className={bodyClassName}>{children}</SheetBaseBody>
  ) : bodyClassName ? (
    <div className={bodyClassName}>{children}</div>
  ) : (
    children
  );

  const headerContent = header ? (
    header
  ) : title || description ? (
    <SheetBaseHeader className={headerClassName}>
      {title ? (
        <SheetBaseTitle>{title}</SheetBaseTitle>
      ) : (
        <SheetBaseTitle className="sr-only">Dialog</SheetBaseTitle>
      )}
      {description && (
        <SheetBaseDescription>{description}</SheetBaseDescription>
      )}
    </SheetBaseHeader>
  ) : null;

  const footerContent = footer ? (
    <SheetBaseFooter className={footerClassName}>{footer}</SheetBaseFooter>
  ) : null;

  return (
    <SheetBaseContext.Provider value={contextValue}>
      {isMobile ? (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side={side}
            showCloseButton={showCloseButton}
            className={cn(
              "flex max-h-[92dvh] flex-col gap-0 overflow-hidden rounded-t-2xl p-4",
              sheetClassName,
            )}
            onOpenAutoFocus={(e) => {
              if (preventAutoFocus) {
                e.preventDefault();
              }
            }}
          >
            {headerContent}
            {bodyContent}
            {footerContent}
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent
            showCloseButton={showCloseButton}
            className={cn(
              "flex max-h-[92dvh] flex-col overflow-hidden p-4 sm:p-6 sm:max-w-lg",
              className,
              dialogClassName,
            )}
          >
            {headerContent}
            {bodyContent}
            {footerContent}
          </DialogContent>
        </Dialog>
      )}
    </SheetBaseContext.Provider>
  );
}

SheetBase.Header = SheetBaseHeader;
SheetBase.Title = SheetBaseTitle;
SheetBase.Description = SheetBaseDescription;
SheetBase.Body = SheetBaseBody;
SheetBase.Footer = SheetBaseFooter;
